<template>
  <div ref="containerRef">
    <AdjustmentsAccountSelect
      v-if="inputMode === 'select'"
      v-model="selectedAccount"
      :focused="props.focused"
      :error="error"
      @create-new-account="handleCreateNewAccount"
      @selected="emit('filled')"
      @tab-back="handleTabBack"
      @tab-forward.prevent.stop="handleTabForward"
    ></AdjustmentsAccountSelect>
    <AdjustmentsAccountCreate
      v-if="inputMode === 'create'"
      v-model="selectedAccount"
      :error="error"
    ></AdjustmentsAccountCreate>
    <AdjustmentsAccountEditName
      v-if="inputMode === 'edit-name'"
      v-model="selectedAccount"
      :error="error"
    ></AdjustmentsAccountEditName>
    <AdjustmentsAccountFilled
      v-if="inputMode === 'filled'"
      :account="selectedAccount"
      :disabled="disabled"
      @edit-selected-account="handleEditSelectedAccount"
    >
    </AdjustmentsAccountFilled>
  </div>
</template>

<script setup lang="ts">
import type {
  SubAdjustmentRowAccount,
  SubAdjustmentRowAccountNew,
} from '@app/application/adjustments/types'

import { onClickOutside, useVModel } from '@vueuse/core'
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'

import AdjustmentsAccountSelect from './adjustments-account-select.vue'
import AdjustmentsAccountFilled from './adjustments-account-filled.vue'
import AdjustmentsAccountCreate from './adjustments-account-create.vue'
import AdjustmentsAccountEditName from './adjustments-account-edit-name.vue'
import { useAdjustmentsModalStore } from '@app/application/adjustments/store/adjustment-modal'

defineOptions({
  name: 'AdjustmentsAccountSelector',
})

const modalStore = useAdjustmentsModalStore()
const { companyConfig } = storeToRefs(modalStore)

const editedAccount = ref<boolean>(false)

interface Props {
  disabled?: boolean
  focused?: number
  error?: string
  pinnedState?: 'create' | 'select' | 'filled'
  modelValue?: SubAdjustmentRowAccount
}

const props = withDefaults(defineProps<Props>(), {
  error: '',
  focused: 0,
  pinnedState: undefined,
  modelValue: () => ({} as SubAdjustmentRowAccount),
})

const containerRef = ref<HTMLElement | null>(null)

onClickOutside(containerRef, () => {
  if (editedAccount.value) {
    editedAccount.value = false
  }
})

const emit =
  defineEmits<{
    filled: []
    focused: []
    'update:modelValue': [value: SubAdjustmentRowAccount]
    'tab-forward': [e: Event]
  }>()

const selectedAccount = useVModel(props, 'modelValue', emit, {
  passive: true,
  deep: true,
})

watch(selectedAccount, () => {
  editedAccount.value = false
})

const handleEditSelectedAccount = (value: boolean) => {
  editedAccount.value = value
  emit('focused')
}

const handleCreateNewAccount = (account: SubAdjustmentRowAccountNew) => {
  selectedAccount.value = account
}

// Back to the filled state if possible
const handleTabBack = () => {
  if (editedAccount.value) {
    editedAccount.value = false
  }
}

/**
 * We need to emit the tab forward event to the parent component
 * and prevent any default behavior of the TAB key in the browser
 * so that the sub-adjustment description field gets programmatically focused.
 * If we don't do that this is the sequence of actions that happens:
 * 1. handleTabForward causes the account selector to change into the filled state
 * 2. The default behavior of the TAB key in the browser focuses the newly added filled state element
 * 3. The filled state element publishes a focused event that results in the state reverting back to select
 * */
const handleTabForward = (e: Event) => {
  if (editedAccount.value) {
    editedAccount.value = false
  }

  emit('tab-forward', e)
}

const inputMode = computed(() => {
  const hasAnEmptyAccount = Object.values(selectedAccount.value ?? {}).every(
    (value) => !value
  )

  if (props.disabled) {
    return 'filled'
  }

  if (companyConfig.value?.requiresMappingListRestriction) {
    if (!selectedAccount.value || editedAccount.value || hasAnEmptyAccount) {
      return 'select'
    } else if (selectedAccount.value?.id === null) {
      return 'edit-name' // New account from mapping list
    } else {
      return 'filled' // Existing account - same as unrestricted mode
    }
  }

  if (!selectedAccount.value || editedAccount.value || hasAnEmptyAccount) {
    return 'select'
  } else if (selectedAccount.value?.id === null) {
    return 'create'
  } else {
    return 'filled'
  }
})
</script>
